<template>
  <div class="video-message" :class="msgItem?.isme ? 'me' : 'others'">
    <div
      class="video-card"
      :class="{ 'video-card--empty': !videoComputed.link }"
      :style="mediaStyle">
      <div class="video-preview" :style="previewStyle" @click="togglePlay">
        <video
          v-if="videoComputed.link"
          ref="videoRef"
          class="video-player"
          :src="videoComputed.link"
          :poster="videoComputed.cover"
          preload="metadata"
          playsinline
          webkit-playsinline
          @play="isPlaying = true"
          @pause="isPlaying = false"
          @ended="isPlaying = false" />
        <div v-else class="video-cover video-cover--placeholder">
          <span>视频</span>
        </div>

        <div class="video-mask"></div>

        <div v-if="!isPlaying && videoComputed.link" class="video-play">
          <div class="video-play__icon">▶</div>
        </div>

        <div v-if="formatDuration(videoComputed.duration)" class="video-badge video-badge--left">
          {{ formatDuration(videoComputed.duration) }}
        </div>
        <div v-if="formatFileSize(videoComputed.video_size)" class="video-badge video-badge--right">
          {{ formatFileSize(videoComputed.video_size) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject, ref } from "vue";

export default {
  name: "CommonVideoMessage",
  setup() {
    const msgItem = inject("msgItem");
    const videoRef = ref(null);
    const isPlaying = ref(false);

    const videoComputed = computed(() => {
      const raw = msgItem?.msg || msgItem?.submsg || "{}";
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.error("解析视频消息失败", error);
        return {};
      }
    });

    const mediaStyle = {
      "--m-media-card-width": "228px",
      "--m-media-card-ratio": "5 / 3",
    };

    const previewStyle = computed(() => {
      const width = Number(videoComputed.value?.size?.width);
      const height = Number(videoComputed.value?.size?.height);
      if (width > 0 && height > 0) {
        return {
          aspectRatio: `${width} / ${height}`,
        };
      }
      return {
        aspectRatio: "9 / 16",
      };
    });

    const togglePlay = async () => {
      const videoEl = videoRef.value;
      if (!videoEl || !videoComputed.value.link) {
        return;
      }

      if (videoEl.paused) {
        try {
          await videoEl.play();
        } catch (error) {
          console.error("视频播放失败", error);
        }
        return;
      }

      videoEl.pause();
    };

    const formatDuration = (duration) => {
      const total = Number(duration);
      if (!Number.isFinite(total) || total <= 0) {
        return "";
      }

      const seconds = Math.ceil(total / 1000);
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const restSeconds = seconds % 60;

      const pad = (value) => String(value).padStart(2, "0");
      if (hours > 0) {
        return `${pad(hours)}:${pad(minutes)}:${pad(restSeconds)}`;
      }
      return `${pad(minutes)}:${pad(restSeconds)}`;
    };

    const formatFileSize = (size) => {
      const bytes = Number(size);
      if (!Number.isFinite(bytes) || bytes <= 0) {
        return "";
      }
      if (bytes < 1024) {
        return `${bytes}B`;
      }
      if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)}KB`;
      }
      return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
    };

    return {
      msgItem,
      videoRef,
      isPlaying,
      videoComputed,
      previewStyle,
      togglePlay,
      formatDuration,
      formatFileSize,
      mediaStyle,
    };
  },
};
</script>

<style lang="scss" scoped>
.video-message {
  display: flex;
  max-width: 100%;
}

.video-message.me {
  justify-content: flex-end;
}

.video-message.others {
  justify-content: flex-start;
}

.video-card {
  width: var(--m-media-card-width, 200px);
  max-width: 100%;
  user-select: none;
}

.video-preview {
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: var(--m-media-card-ratio, 5 / 3);
  border-radius: 10px;
  background: linear-gradient(180deg, #1f2937 0%, #0f172a 100%);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
  cursor: pointer;
}

.video-player,
.video-cover,
.video-cover--placeholder {
  width: 100%;
  height: 100%;
  display: block;
}

.video-player {
  object-fit: cover;
  background: #0f172a;
}

.video-cover {
  object-fit: cover;
}

.video-cover--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  letter-spacing: 0.12em;
  background: radial-gradient(circle at top, rgba(255, 255, 255, 0.12), transparent 38%),
    linear-gradient(135deg, #334155 0%, #0f172a 100%);
}

.video-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.08) 0%,
    rgba(15, 23, 42, 0.08) 45%,
    rgba(15, 23, 42, 0.35) 100%
  );
  pointer-events: none;
}

.video-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.video-play__icon {
  width: 58px;
  height: 58px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 18px;
  padding-left: 4px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, background 0.2s ease;
}

.video-preview:hover .video-play__icon {
  transform: scale(1.06);
  background: rgba(255, 255, 255, 0.24);
}

.video-badge {
  position: absolute;
  bottom: 10px;
  z-index: 1;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  pointer-events: none;
}

.video-badge--left {
  left: 10px;
}

.video-badge--right {
  right: 10px;
}

.video-meta {
  margin-top: 8px;
  padding: 0 2px;
}

.video-meta__title {
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.video-meta__desc {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
}

.video-card--empty {
  opacity: 0.9;
}
</style>
